import { useState } from "react"
import {
  Avatar,
  Button,
  Paper,
  Stack,
  Text,
  ActionIcon,
  LoadingOverlay,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import { Trans, useTranslation } from "react-i18next"
import useUpload from "../../../hooks/useUpload"
import type { AdminUser } from "../models/adminUser"

interface Props {
  user: AdminUser
  canManage: boolean
  onResetPassword: () => void
}

export default function ProfileInfoCard({ user, canManage, onResetPassword }: Props) {
  const { t } = useTranslation()
  const changeAvatarMutation = { isPending: false, mutateAsync: async (_args: { userId: number; file: File }) => {} }
  const [uploading, { open: openUploading, close: closeUploading }] = useDisclosure(false)
  const [avatarVersion, setAvatarVersion] = useState(0)

  const handleAvatarUpload = useUpload(
    async (files: File | FileList) => {
      const file = files as File
      openUploading()
      try {
        await changeAvatarMutation.mutateAsync({ userId: user.id, file })
        setAvatarVersion((v) => v + 1)
        notifications.show({
          color: "green",
          title: t("common.success"),
          message: t("adminUsers.avatarUpdateSuccess"),
        })
      } catch (err) {
        notifications.show({
          color: "red",
          title: t("common.error"),
          message: err instanceof Error ? err.message : t("common.error"),
        })
      } finally {
        closeUploading()
      }
    },
    { accept: "image/*" },
  )

  return (
    <Paper withBorder p="lg" ta="center" h="100%">
      <div style={{ position: "relative", display: "inline-block" }}>
        <LoadingOverlay visible={uploading} overlayProps={{ radius: "sm" }} />
        <Avatar
          src={(user.avatar || "") + (avatarVersion ? `?v=${avatarVersion}` : "")}
          size={100}
          radius={100}
          mx="auto"
          mb="sm"
          color="blue"
        >
          {user.first_name?.[0]}
          {user.last_name?.[0]}
        </Avatar>
        {canManage && (
          <ActionIcon
            size="lg"
            radius="xl"
            style={{
              position: "absolute",
              bottom: 4,
              right: 4,
              background: "var(--mantine-color-blue-6)",
              color: "white",
            }}
            onClick={handleAvatarUpload}
            loading={changeAvatarMutation.isPending}
          >
            <i className="fa fa-camera" />
          </ActionIcon>
        )}
      </div>
      <Text fw={700} size="lg">
        {user.full_name}
      </Text>
      <Text size="sm" c="dimmed">
        @{user.username}
      </Text>

      <Stack gap="xs" mt="md" align="flex-start">
        <Text size="sm" w="100%">
          <Text component="span" fw={600} size="xs" tt="uppercase" c="dimmed">
            {t("adminUsers.agency")}
          </Text>
          <br />
          {user.agency_name ?? (
            <Text component="span" size="sm" fs="italic" c="gray.5">
              <Trans i18nKey="common.notFilled" />
            </Text>
          )}
        </Text>
        <Text size="sm" w="100%">
          <Text component="span" fw={600} size="xs" tt="uppercase" c="dimmed">
            {t("adminUsers.email")}
          </Text>
          <br />
          {user.email}
        </Text>
      </Stack>

      <Stack gap="xs" mt="lg">
        <Button
          fullWidth
          variant="subtle"
          onClick={onResetPassword}
          leftSection={<i className="fa fa-key" />}
        >
          {t("adminUsers.resetPassword")}
        </Button>
      </Stack>
    </Paper>
  )
}
